import { useEffect, useMemo, useState } from 'react';
import type { TaxOption } from '@repo/api-client/dtos';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';

/**
 * Output shape the caller writes back into the form. `kind` is only
 * meaningful for invoice-level taxes (a per-line tax is always
 * additive; withholding is invoice-wide on the wire's TaxConfig).
 */
export interface TaxValue {
  rate: string;
  label: string;
  kind: 'add' | 'subtract';
}

interface TaxDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  initial: TaxValue | null;
  /** `'line'` hides withholding; `'invoice'` exposes it. */
  level: 'line' | 'invoice';
  presets?: TaxOption[];
  onSave: (next: TaxValue | null) => void;
}

const QUICK_RATES = ['5', '7', '8.875', '10', '15', '20'];

export function TaxDialog({
  open,
  onOpenChange,
  initial,
  level,
  presets,
  onSave,
}: TaxDialogProps) {
  const [rateText, setRateText] = useState<string>(() => initial?.rate ?? '');
  const [label, setLabel] = useState<string>(() => initial?.label ?? 'Tax');
  const [kind, setKind] = useState<'add' | 'subtract'>(() => initial?.kind ?? 'add');

  useEffect(() => {
    if (open) {
      setRateText(initial?.rate ?? '');
      setLabel(initial?.label ?? 'Tax');
      setKind(initial?.kind ?? 'add');
    }
  }, [open, initial]);

  const valid = useMemo(() => {
    const t = rateText.trim();
    if (t === '') return false;
    if (!/^[0-9]+(?:[.,][0-9]+)?$/.test(t)) return false;
    const n = Number.parseFloat(t.replace(',', '.'));
    return Number.isFinite(n) && n > 0 && n <= 100;
  }, [rateText]);

  function handleApply() {
    if (!valid) return;
    onSave({
      rate: rateText.trim().replace(',', '.'),
      label: label.trim() || 'Tax',
      kind: level === 'invoice' ? kind : 'add',
    });
    onOpenChange(false);
  }

  function handleRemove() {
    onSave(null);
    onOpenChange(false);
  }

  function applyPreset(p: TaxOption) {
    setRateText(p.rate);
    setLabel(p.name);
    if (level === 'invoice') setKind(p.kind ?? 'add');
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {level === 'line' ? 'Line tax' : 'Invoice tax'}
          </DialogTitle>
          <DialogDescription>
            {level === 'line'
              ? 'Applied to this row only. Picks per-line tax mode for the whole invoice.'
              : 'Single rate applied to the invoice subtotal.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-center gap-2 rounded-xl border border-line bg-secondary/30 px-4 py-5">
          <Input
            value={rateText}
            onChange={(e) => setRateText(e.target.value)}
            inputMode="decimal"
            autoFocus
            aria-label="Tax rate"
            placeholder="0"
            className="h-auto w-32 border-transparent bg-transparent text-center font-display text-4xl tabular-nums shadow-none focus-visible:bg-card focus-visible:shadow-sm"
          />
          <span className="font-display text-3xl text-muted-foreground">%</span>
        </div>

        <div className="flex flex-wrap gap-2">
          {QUICK_RATES.map((r) => {
            const on = rateText.trim() === r;
            return (
              <button
                key={r}
                type="button"
                onClick={() => setRateText(r)}
                className={cn(
                  'rounded-lg px-3 py-1 text-xs font-medium transition-colors',
                  on
                    ? 'bg-foreground text-background'
                    : 'bg-secondary/60 text-foreground hover:bg-secondary',
                )}
              >
                {r}%
              </button>
            );
          })}
        </div>

        {presets && presets.length > 0 ? (
          <div className="flex flex-col gap-1.5">
            <Label className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              Saved
            </Label>
            <div className="flex flex-wrap gap-2">
              {presets.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => applyPreset(p)}
                  className="rounded-lg border border-line bg-card px-2.5 py-1 text-xs hover:border-line-strong"
                  title={`${p.name} · ${p.rate}% · ${p.kind === 'subtract' ? 'withheld' : 'added'}`}
                >
                  <span className="font-medium text-foreground">{p.name}</span>
                  <span className="ml-1.5 font-mono text-muted-foreground">{p.rate}%</span>
                </button>
              ))}
            </div>
          </div>
        ) : null}

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="tax-label" className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
            Label
          </Label>
          <Input
            id="tax-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Tax"
          />
        </div>

        {level === 'invoice' ? (
          <div className="flex items-center justify-between rounded-lg border border-line bg-secondary/20 px-3 py-2">
            <div className="flex flex-col">
              <span className="text-sm font-medium text-foreground">Withholding</span>
              <span className="text-[11px] text-muted-foreground">
                Subtract from the total instead of adding to it.
              </span>
            </div>
            <Switch
              checked={kind === 'subtract'}
              onCheckedChange={(v) => setKind(v ? 'subtract' : 'add')}
              aria-label="Withhold (subtract from total)"
            />
          </div>
        ) : null}

        <DialogFooter>
          {initial ? (
            <Button variant="ghost" onClick={handleRemove}>
              Remove
            </Button>
          ) : null}
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleApply} disabled={!valid}>
            Apply tax
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
